import { create } from 'zustand';
import { getMe, HustroUser, postPersonalData } from '../services/userService';
import { EncryptedObjectIdString, ObjectIdString } from '../types/common';
import { setCurrentUser } from '../utils/currentUser';

interface UserState {
    me: HustroUser | undefined;
    loading: boolean;
    error: Error | null;
    fetchUser: (getAccessToken: () => Promise<string>) => Promise<void>;
    addOrUpdatePersonalData: (
        getAccessToken: () => Promise<string>,
        encryptedClientId: EncryptedObjectIdString,
        label: string,
        phone: string,
        userId: ObjectIdString,
    ) => Promise<void>;
    reset: () => void;
}

const initialState = {
    me: undefined,
    loading: false,
    error: null,
};

export const useUserStore = create<UserState>((set, get) => ({
    ...initialState,

    fetchUser: async (getAccessToken) => {
        // Skip if another fetch is already in flight or the user is already loaded.
        // An earlier error does NOT block retry — the caller (AuthWrapper's useEffect)
        // may re-fire on auth/token changes, and we want the next attempt to succeed.
        if (get().loading || get().me) {
            return;
        }

        set({ loading: true, error: null });
        try {
            const token = await getAccessToken();
            const data = await getMe(token, {});

            set({ me: data, loading: false });
            setCurrentUser({ id: data._id, email: data.email });
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Unknown error');
            set({ error, loading: false });
        }
    },

    addOrUpdatePersonalData: async (
        getAccessToken,
        encryptedClientId,
        label,
        phone,
        userId,
    ) => {
        const token = await getAccessToken();
        await postPersonalData(token, {
            encryptedClientId,
            label,
            userId,
            phone,
        });

        try {
            const updatedUserData = await getMe(token, {});
            set({ me: updatedUserData });
        } catch (err) {
            // postPersonalData succeeded but refetch failed — clear `me` so the next
            // fetchUser() call retries (it early-returns while `me` is truthy).
            set({ me: undefined });
            throw err;
        }
    },

    reset: () => set(initialState),
}));
